import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { AnomalyLabel, LabelAction } from './types';

export default function Labels({
  labels,
  open,
  onClose,
  onAction,
}: {
  labels: AnomalyLabel[];
  open: boolean;
  onClose: () => void;
  onAction: (command: LabelAction) => Promise<void>;
}) {
  const [selected, setSelected] = useState('');
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const label = labels.find((l) => l.id === selected);
  async function action(type: LabelAction['action']) {
    if (!label || busy) return;
    setBusy(true);
    setError('');
    try {
      await onAction({
        labelId: label.id,
        expectedVersion: label.version,
        action: type,
        ...(type === 'rename' ? { name } : {}),
        ...(type === 'merge' ? { targetId: target } : {}),
      });
      setSelected('');
      setTarget('');
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value && !busy) onClose();
      }}
    >
      <DialogContent className="anomaly-label-dialog">
        <DialogHeader>
          <DialogTitle>Labels</DialogTitle>
          <DialogDescription>
            Fælles forklaringer til alle anlæg. Opret nye labels, når du vurderer en hændelse.
          </DialogDescription>
        </DialogHeader>
        <fieldset disabled={busy}>
          <label htmlFor="manage-anomaly-label" className="anomaly-field-label">
            Vælg label
          </label>
          <select
            id="manage-anomaly-label"
            className="anomaly-input"
            value={selected}
            onChange={(e) => {
              setSelected(e.target.value);
              setName(labels.find((l) => l.id === e.target.value)?.name || '');
              setError('');
            }}
          >
            <option value="">Vælg…</option>
            {labels
              .filter((l) => !l.mergedInto)
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                  {l.archived ? ' (arkiveret)' : ''}
                </option>
              ))}
          </select>
          {label && (
            <div className="anomaly-label-edit">
              <label htmlFor="rename-anomaly-label" className="anomaly-field-label">
                Navn
              </label>
              <input
                id="rename-anomaly-label"
                className="anomaly-input"
                maxLength={60}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <div className="anomaly-save-actions">
                <button
                  className="anomaly-button"
                  disabled={!name.trim()}
                  onClick={() => void action('rename')}
                >
                  Gem navn
                </button>
                <button
                  className="anomaly-button"
                  onClick={() => void action(label.archived ? 'restore' : 'archive')}
                >
                  {label.archived ? 'Gendan' : 'Arkivér'}
                </button>
              </div>
              <label htmlFor="merge-anomaly-label" className="anomaly-field-label">
                Sammenlæg med
              </label>
              <select
                id="merge-anomaly-label"
                className="anomaly-input"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">Vælg en anden label…</option>
                {labels
                  .filter((l) => l.id !== label.id && !l.archived && !l.mergedInto)
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
              </select>
              <p className="anomaly-muted">
                Tidligere annotationer vises under den valgte label. Historikken bevares.
              </p>
              <button
                className="anomaly-button"
                disabled={!target}
                onClick={() => void action('merge')}
              >
                Sammenlæg labels
              </button>
            </div>
          )}
          {busy && <p role="status">Gemmer…</p>}
          {error && (
            <p role="alert" className="anomaly-error">
              {error}
            </p>
          )}
        </fieldset>
      </DialogContent>
    </Dialog>
  );
}
